import { RecordingRequestObjectFeed } from 'constants/recordingsConstants';
import Pagination from 'containers/Pagination/PaginationContainer';
import RecordingsToolbar from 'containers/Recordings/RecordingsToolbarContainer';
import _get from 'lodash/get';
import React from 'react';
import intl from 'react-intl-universal';
import { RecordingDataArray } from 'interfaces/RecordingDataItem';
import * as styles from '../Recordings.scss';
import RecordingsListItemView from '../RecordingsListItemView';
import RecordingsTileView from '../RecordingsTileView';
import { getUrlParameter } from 'utils/url-utils';
import classnames from 'classnames';
import Icon from 'components/Common/Icon';
import { useMount } from 'hooks/useMount';

export interface Props {
  isGridLayout: boolean;
  limit: number;
  isLoading: boolean;
  recordingData: RecordingDataArray;
  totalCount: number;
  handleFetchFeedData: () => void;
  handleResetRecordingProps: Function;
  handlePageChange: (pageNum: number) => void;
  pageType: RecordingRequestObjectFeed;
  error: object;
  canUpload: boolean;
  openUpload: () => void;
  processingUploadCount: number;
  getUploadsStatus: () => void;
}

let shouldHandlePageChange = false;

const Recordings = (props: Props) => {
  const [isProcessingUpload, setIsProcessingUpload] = React.useState(props.processingUploadCount > 0);
  const [uploadIsComplete, setUploadIsComplete] = React.useState(false);
  const [previousProcessingCount, setPreviousProcessingCount] = React.useState(props.processingUploadCount);
  const isMounted = useMount();
  const {
    isLoading,
    pageType,
    recordingData,
    error,
    canUpload,
    processingUploadCount,
    getUploadsStatus,
    handleFetchFeedData,
    handleResetRecordingProps,
    isGridLayout,
    totalCount,
    limit
  } = props;
  const initialSearchId = getUrlParameter('meeting') || undefined;

  React.useEffect(
    () => {
      if (pageType === RecordingRequestObjectFeed.FEED_ALL) {
        getUploadsStatus();
      }
    },
    [pageType]
  );

  React.useEffect(
    () => {
      shouldHandlePageChange = true;
      if (!initialSearchId) {
        handleFetchFeedData();
      }
      return () => {
        shouldHandlePageChange = false;
        handleResetRecordingProps();
      };
    },
    []
  );

  React.useEffect(
    () => {
      if (isProcessingUpload && processingUploadCount === 0) {
        setUploadIsComplete(true);
        setIsProcessingUpload(false);
        setTimeout(
          () => {
            if (isMounted.current) {
              setUploadIsComplete(false);
            }
          },
          5000
        );
      }
      if (processingUploadCount) {
        setIsProcessingUpload(true);
      }
    },
    [processingUploadCount, isProcessingUpload]
  );

  React.useEffect(
    () => {
      if (canUpload && processingUploadCount < previousProcessingCount) {
        handleFetchFeedData();
      }
      if (processingUploadCount !== previousProcessingCount) {
        setPreviousProcessingCount(processingUploadCount);
      }
    },
    [canUpload, processingUploadCount, previousProcessingCount]
  );

  const handlePageChange = (pageNum: number) => {
    // prevent the fetch feed request on the pageChange event fired on unload
    if (shouldHandlePageChange) {
      props.handlePageChange(pageNum + 1);
    }
  };

  const getUploadStatus = () => {
    if (uploadIsComplete) {
      return (
        <div className={classnames(styles.uploadStatus, styles.uploadSuccess)}>
          {intl.get('uploadStatusComplete')}
        </div>
      );
    }
    if (processingUploadCount) {
      return (
        <div className={styles.uploadStatus}>
          {processingUploadCount === 1
            ? intl.get('uploadStatusProcessingVideo')
            : intl.get('uploadStatusProcessingVideos', { count: processingUploadCount })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.container}>
      <RecordingsToolbar
        pageType={pageType}
        initialSearchId={initialSearchId}
        canUpload={canUpload}
        uploadStatus={getUploadStatus()}
      />
      {_get(error, 'message') === 'FORBIDDEN' ? (
        <div className="contentBody">
          <div className={classnames(styles.forbidden, styles.loading)}>
            <Icon classes={['icon-record']} />
            <h4>{intl.get('recordingsNoAccess')}</h4>
          </div>
        </div>
      ) : (
        <div className="contentBody">
          {
            (isGridLayout) ?
              <RecordingsTileView isLoading={isLoading} pageType={pageType} recordingData={recordingData || []}/> :
              <RecordingsListItemView isLoading={isLoading} pageType={pageType} recordingData={recordingData || []}/>
          }
        </div>
      )}
      {
        /* Always render <Pagination> because componentWillUnmount will trigger onPageChange which can trigger an
         * unwanted data request creating a possible race condition */
      }
      <Pagination
        numOfPages={Math.max(Math.ceil(totalCount / limit), 1)}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default Recordings;
